/**
 * Category chips shown under the search input.
 *
 * Each chip is a toggle for one category with its live match count. Clicking
 * (or Space/Enter when focused) toggles that category in `filters.entityTypes`.
 * Entity-type counts come from the backend and reflect the current query +
 * module/date filters but ignore the entity-type filter itself, so all types
 * stay visible.
 *
 * The row leads with the two navigation categories — Modules and Pages, matched
 * client-side (see `useGlobalSearch`) — separated from the entity types by a
 * hairline rule, because they are a different kind of destination: places you
 * go rather than records you open.
 */
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../config/iconMap';
import { cn } from '../../../lib/utils';
import type { SearchFacet } from '../../services/searchApi';
import { isNavFacet } from './searchConfig';

interface GlobalSearchFacetsProps {
  facets: SearchFacet[];
  /** Currently selected categories (filters.entityTypes). */
  selected: string[];
  /** Toggle a single category in the selection. */
  onToggle: (entityType: string) => void;
}

/** Humanize an entity_type table name as a fallback when no i18n label exists. */
function fallbackLabel(entityType: string): string {
  const parts = entityType.split('_');
  const label = parts.length > 1 ? parts.slice(1).join(' ') : entityType;
  return label.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function GlobalSearchFacets({
  facets,
  selected,
  onToggle,
}: GlobalSearchFacetsProps) {
  const { t } = useTranslation('common');

  if (facets.length === 0) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b"
      role="group"
      aria-label={t('globalSearch.facetsLabel', 'Filter by type')}
      data-testid="global-search-facets"
    >
      {facets.map((facet, index) => {
        const isActive = selected.includes(facet.entity_type);
        // Rule between the navigation categories and the entity types.
        const startsEntityTypes =
          index > 0 &&
          !isNavFacet(facet.entity_type) &&
          isNavFacet(facets[index - 1].entity_type);
        const label = facet.label
          ? t(facet.label, { defaultValue: fallbackLabel(facet.entity_type) })
          : fallbackLabel(facet.entity_type);
        const icon = facet.icon ? getIcon(facet.icon) : null;

        return (
          <Fragment key={facet.entity_type}>
            {startsEntityTypes && (
              <span className="h-5 w-px bg-border" aria-hidden="true" />
            )}
            <button
              type="button"
              data-testid="search-facet-chip"
              data-entity-type={facet.entity_type}
              data-active={isActive || undefined}
              aria-pressed={isActive}
              onClick={() => onToggle(facet.entity_type)}
              // The search overlay binds Enter/Space to result navigation; keep
              // those keys local so a focused chip toggles instead of navigating.
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
              }}
              className={cn(
                'inline-flex items-center gap-1.5 h-8 rounded-full border px-2.5 text-xs font-medium',
                // A 32px pill is the right density on a pointer; on a coarse
                // pointer it grows to the 44px minimum rather than staying a
                // hard-to-hit target in a wrapped row (STYLE_GUIDE § Responsive).
                'touch:min-h-11',
                'transition-colors focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
                isActive
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-muted text-foreground hover:bg-accent',
              )}
            >
              {icon && (
                <span className="[&>svg]:h-3.5 [&>svg]:w-3.5 flex-shrink-0">
                  {icon}
                </span>
              )}
              <span className="truncate">{label}</span>
              <span
                className={cn(
                  'tabular-nums',
                  isActive
                    ? 'text-primary-foreground/80'
                    : 'text-muted-foreground',
                )}
              >
                {facet.count}
              </span>
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}
